import React from 'react';
import {HStack, Heading, Text, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';

import {StackParamList} from '../../../../navigators';
import InternalTemplate from '../../../../components/templates/internal';
import {message} from '../../../../utils/messages';
import {useSalesmanContext} from '../../../../context/salesman';
import {
  ApprovedIcon,
  BolivarIcon,
  DollarIcon,
  RejectedIcon,
  WaitingIcon,
} from '../../../../components/icons';
import {OrderStatusEnum} from '../../../../constants/enumerations';
import {PrimaryButton} from '../../../../components/buttons';
import {IOrderDetail} from '../../../../interfaces/dtos';

type ScreenProps = NativeStackScreenProps<
  StackParamList,
  'SalesmanOrderSummary'
>;

const itemValueWidth = 150;

interface IOrderDetailProps {
  orderDetail: IOrderDetail | null;
}

const StatusHeader = ({orderDetail}: IOrderDetailProps) => {
  return (
    <VStack>
      <Heading color="#06355E" fontSize={20}>
        {message(
          `salesman-order-summary-heading-status-${orderDetail?.status}`,
        )}
      </Heading>
      <Text color="#99A2B4">
        {' '}
        {message(
          `salesman-order-summary-heading-departament-${orderDetail?.departament}`,
        )}
      </Text>
    </VStack>
  );
};

const StatusIcon = ({orderDetail}: IOrderDetailProps) => {
  if (orderDetail) {
    if (orderDetail.status === OrderStatusEnum.waiting) {
      return <WaitingIcon size={40} />;
    }
    if (orderDetail.status === OrderStatusEnum.rejected) {
      return <RejectedIcon size={40} />;
    }
  }
  return <ApprovedIcon size={40} />;
};

const ItemValue = (props: {
  currency: 'bs' | '$';
  value: string;
  label: string;
}) => {
  const {currency, value, label} = props;
  return (
    <HStack mt={1} ml={3} justifyContent={'space-between'}>
      <Heading fontSize={16} color="#06355E">
        {message(label)}
      </Heading>
      <HStack width={itemValueWidth} justifyContent={'space-between'}>
        {currency === 'bs' && <BolivarIcon />}
        {currency === '$' && <BolivarIcon />}
        <Text color="#06355E" fontSize={18} textAlign={'right'} minWidth={120}>
          {value}
        </Text>
      </HStack>
    </HStack>
  );
};

const SummaryHeader = ({orderDetail}: IOrderDetailProps) => {
  if (orderDetail) {
    return (
      <HStack mt={5} justifyContent={'space-between'}>
        <VStack>
          <Heading color="#4C4C4C" fontSize={20}>
            {orderDetail.id}
          </Heading>
          <Text color="#99A2B4">{orderDetail.sumary}</Text>
        </VStack>
        <VStack>
          <HStack width={itemValueWidth} justifyContent={'space-between'}>
            <DollarIcon />
            <Text
              color="#06355E"
              fontSize={18}
              textAlign={'right'}
              minWidth={120}>
              {orderDetail.amount.foreign}
            </Text>
          </HStack>
          <HStack width={itemValueWidth} justifyContent={'space-between'}>
            <BolivarIcon />
            <Text
              color="#06355E"
              fontSize={18}
              textAlign={'right'}
              minWidth={120}>
              {orderDetail.amount.local}
            </Text>
          </HStack>
        </VStack>
      </HStack>
    );
  }
  return <></>;
};

const OrderSummaryScreen = ({navigation}: ScreenProps) => {
  const {orderDetail} = useSalesmanContext();

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoDetailPress = () => {
    navigation.navigate('SalesmanOrderDetail');
  };

  return (
    <InternalTemplate
      title={'salesman-order-summary'}
      contentPaddingX="5"
      backButton={{
        onPress: () => handleGoBack(),
        label: 'go-back',
        usage: 'external',
      }}>
      <VStack justifyContent="flex-start" mb={40}>
        {orderDetail && (
          <>
            <HStack justifyContent={'space-between'}>
              <StatusHeader orderDetail={orderDetail} />
              <StatusIcon orderDetail={orderDetail} />
            </HStack>
            <SummaryHeader orderDetail={orderDetail} />
            <VStack mt={5}>
              <Heading fontSize={18} color="#06355E" mb={3}>
                {message('order-summary-balance-foreigns-label')}
              </Heading>
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-total'}
                value={orderDetail.balance.foreign.total}
              />
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-paid'}
                value={orderDetail.balance.foreign.paid}
              />
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-balance'}
                value={orderDetail.balance.foreign.balance}
              />
            </VStack>
            <VStack mt={5}>
              <Heading fontSize={18} color="#06355E" mb={3}>
                {message('order-summary-balance-local-label')}
              </Heading>
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-total'}
                value={orderDetail.balance.local.total}
              />
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-paid'}
                value={orderDetail.balance.local.paid}
              />
              <ItemValue
                currency="$"
                label={'order-summary-balance-item-foreign-balance'}
                value={orderDetail.balance.local.balance}
              />
            </VStack>
            <VStack mt={16}>
              <PrimaryButton
                label={'order-summary-button-go-detail-label'}
                onPress={handleGoDetailPress}
              />
            </VStack>
          </>
        )}
      </VStack>
    </InternalTemplate>
  );
};

export default OrderSummaryScreen;
